import React, { useEffect, useMemo, useState } from "react";
import api, { compressImage, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertTriangle, Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

const ISSUES = [
  ["missing", "Missing file"], ["corrupted", "File won't open / corrupted"], ["wrong", "Wrong file"],
  ["duplicate", "Duplicate charge"], ["other", "Other"],
];

export const ReportProblemButton = ({ track }) => {
  const [open, setOpen] = useState(false);
  const [issue, setIssue] = useState("");
  const [message, setMessage] = useState("");
  const [shots, setShots] = useState([]);
  const [busy, setBusy] = useState(false);
  const thumbs = useMemo(() => shots.map((f) => URL.createObjectURL(f)), [shots]);
  useEffect(() => () => thumbs.forEach((u) => URL.revokeObjectURL(u)), [thumbs]);
  const reset = () => { setIssue(""); setMessage(""); setShots([]); };

  const addShots = async (list) => {
    const imgs = Array.from(list).filter((f) => f.type.startsWith("image/"));
    const room = 3 - shots.length;
    if (imgs.length > room) toast.error("Up to 3 screenshots");
    const compressed = await Promise.all(imgs.slice(0, room).map((f) => compressImage(f, { maxDim: 1600, quality: 0.8 })));
    setShots((s) => [...s, ...compressed]);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("issue", issue); fd.append("message", message);
      shots.forEach((f) => fd.append("screenshots", f, f.name));
      const { data } = await api.post(`/purchases/${track.id}/report`, fd);
      toast.success(`Report #${data.report_id} sent. A confirmation is on its way to ${data.email}`);
      setOpen(false); reset();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send the report"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid={`report-problem-${track.id}`} onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 rounded-full text-xs text-[#6b7280] hover:text-[#f59e0b] transition-colors py-1.5">
        <AlertTriangle className="w-3.5 h-3.5" /> Report a file problem
      </button>
      <Dialog open={open} onOpenChange={(v) => { if (busy) return; setOpen(v); if (!v) reset(); }}>
        <DialogContent data-testid="report-dialog" className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6] max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Report a file problem</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#9ca3af]">
            “{track.title}”. We'll get your order details automatically and reply within 2 business days.
          </DialogDescription>
          <div className="space-y-3">
            <Select value={issue} onValueChange={setIssue}>
              <SelectTrigger data-testid="report-issue-select" className="bg-[#0a0a0a] border-[#2a2a2a]"><SelectValue placeholder="What went wrong?" /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6]">
                {ISSUES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`report-issue-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <textarea data-testid="report-message-input" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={3000} rows={4}
              placeholder={issue === "other" ? "Describe the problem (required)" : "Anything that helps, e.g. which file, what error you see (optional)"}
              className="w-full rounded-lg bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#e2ff31] outline-none p-3 text-sm resize-none" />
            <div>
              <div className="flex flex-wrap gap-2">
                {shots.map((f, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-[#2a2a2a]">
                    <img src={thumbs[i]} alt="" className="w-full h-full object-cover" />
                    <button data-testid={`report-shot-remove-${i}`} onClick={() => setShots((s) => s.filter((_, j) => j !== i))}
                      className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {shots.length < 3 && (
                  <label data-testid="report-shot-add" className="w-16 h-16 rounded-lg border border-dashed border-[#2a2a2a] hover:border-[#e2ff31] flex items-center justify-center cursor-pointer transition-colors">
                    <ImagePlus className="w-5 h-5 text-[#6b7280]" />
                    <input data-testid="report-shot-input" type="file" accept="image/*" multiple className="hidden"
                      onChange={(e) => { addShots(e.target.files); e.target.value = ""; }} />
                  </label>
                )}
              </div>
              <p className="text-xs text-[#6b7280] mt-1.5">Screenshots optional, up to 3.</p>
            </div>
            <button data-testid="report-submit" onClick={submit} disabled={busy || !issue || (issue === "other" && message.trim().length < 5)}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold py-3 transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send report"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
